import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getMassMessages,
  getMassMessage,
  createMassMessage,
  updateMassMessage,
  archiveMassMessage,
  cancelMassMessage,
  addMassMessageAttachments,
  removeMassMessageAttachment,
  getMassMessageMetrics,
  getMerchantPlan,
} from '../../services/massMessages';

/**
 * Hook to fetch mass messages for an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID to filter by
 * @param {Object} options.useQueryOptions - Additional react-query options
 */
export function useEventMassMessages({ eventId, useQueryOptions = {} } = {}) {
  return useQuery(
    ['event-mass-messages', eventId],
    async () => getMassMessages({ query: { eventId }, loadingStatusOff: true }),
    {
      enabled: !!eventId,
      refetchOnWindowFocus: false,
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to fetch standalone mass messages (not event-specific) with pagination
 * @param {Object} options
 * @param {number} [options.offset=0] - Pagination offset
 * @param {number} [options.limit=10] - Items per page
 * @param {string[]} [options.statuses] - Filter by one or more statuses
 * @param {string} [options.search] - Search by content
 * @param {Object} [options.useQueryOptions] - Additional react-query options
 */
export function useMassMessages({ offset = 0, limit = 10, statuses, search, useQueryOptions = {} } = {}) {
  const query = { offset, limit };
  if (statuses?.length) query.statuses = statuses;
  if (search) query.search = search;

  return useQuery(
    ['mass-messages', { offset, limit, statuses, search }],
    async () => getMassMessages({ query, loadingStatusOff: true }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to fetch a single mass message by ID
 * @param {Object} options
 * @param {string|number} options.massMessageId - Mass message ID
 * @param {Object} [options.useQueryOptions] - Additional react-query options
 */
export function useMassMessage({ massMessageId, useQueryOptions = {} } = {}) {
  return useQuery(
    ['mass-message', massMessageId],
    async () => getMassMessage({ massMessageId, loadingStatusOff: true }),
    {
      enabled: !!massMessageId,
      refetchOnWindowFocus: false,
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to create a new mass message
 * Invalidates event mass messages list and standalone list on success
 */
export function useCreateMassMessage() {
  const queryClient = useQueryClient();
  return useMutation(data => createMassMessage({ data, loadingStatusOff: true }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['event-mass-messages']);
      queryClient.invalidateQueries(['mass-messages']);
    },
  });
}

/**
 * Hook to update an existing mass message
 * Invalidates event mass messages list, standalone list, and single message on success
 */
export function useUpdateMassMessage() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ massMessageId, data }) => updateMassMessage({ massMessageId, data, loadingStatusOff: true }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['event-mass-messages']);
        queryClient.invalidateQueries(['mass-messages']);
        queryClient.invalidateQueries(['mass-message', variables.massMessageId]);
      },
    },
  );
}

/**
 * Hook to archive (delete) a mass message
 * Invalidates event mass messages list and standalone list on success
 */
export function useArchiveMassMessage() {
  const queryClient = useQueryClient();
  return useMutation(massMessageId => archiveMassMessage({ massMessageId, loadingStatusOff: true }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['event-mass-messages']);
      queryClient.invalidateQueries(['mass-messages']);
    },
  });
}

/**
 * Hook to cancel a scheduled mass message
 * Invalidates event mass messages list and standalone list on success
 */
export function useCancelMassMessage() {
  const queryClient = useQueryClient();
  return useMutation(massMessageId => cancelMassMessage({ massMessageId, loadingStatusOff: true }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['event-mass-messages']);
      queryClient.invalidateQueries(['mass-messages']);
    },
  });
}

/**
 * Hook to add attachments to a mass message
 */
export function useAddMassMessageAttachments() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ massMessageId, fileUrls }) => addMassMessageAttachments({ massMessageId, fileUrls }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['mass-messages']);
        queryClient.invalidateQueries(['mass-message', variables.massMessageId]);
      },
    },
  );
}

/**
 * Hook to remove an attachment from a mass message
 */
export function useRemoveMassMessageAttachment() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ massMessageId, attachmentId }) => removeMassMessageAttachment({ massMessageId, attachmentId }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['mass-messages']);
        queryClient.invalidateQueries(['mass-message', variables.massMessageId]);
      },
    },
  );
}

/**
 * Hook to fetch aggregate metrics for mass messages
 * @param {Object} options
 * @param {string} [options.status] - Filter by status
 * @param {string} [options.scheduledAfter] - ISO date string
 * @param {string} [options.scheduledBefore] - ISO date string
 * @param {Object} [options.useQueryOptions] - Additional react-query options
 */
export function useMassMessageMetrics({ status, scheduledAfter, scheduledBefore, useQueryOptions = {} } = {}) {
  const query = {};
  if (status) query.status = status;
  if (scheduledAfter) query.scheduledAfter = scheduledAfter;
  if (scheduledBefore) query.scheduledBefore = scheduledBefore;

  return useQuery(
    ['mass-message-metrics', { status, scheduledAfter, scheduledBefore }],
    async () => getMassMessageMetrics({ query, loadingStatusOff: true }),
    {
      refetchOnWindowFocus: false,
      ...useQueryOptions,
    },
  );
}

/**
 * Hook to fetch the merchant plan (includes mass messaging limit)
 */
export function useMerchantPlan() {
  return useQuery(['merchant-plan'], () => getMerchantPlan(), {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
}
